/**
 * Per-channel text format — single source of truth for "is this channel's text
 * Markdown, or is it literal?".
 *
 * Isomorphic on purpose (type-only import, no server/node deps), like
 * `channel-text-limits.ts`: the send pipeline, API routes, and client UI all
 * read the same answer without the client pulling in adapter/registry code.
 *
 * Total `Record<ChannelType, ...>` (not Partial): adding a new ChannelType
 * fails typecheck here, forcing a deliberate format decision instead of a
 * silent default.
 */

import type { ChannelType } from '../types'

/**
 * `markdown` — replies are authored as a restricted Markdown subset and
 *   rendered through `react-markdown` (widget + inbox).
 * `plain`    — text is literal. WhatsApp belongs here too: its native `*b*` /
 *   `_i_` / `~s~` markers are NOT Markdown, and nothing may rewrite them —
 *   flattening `*bold*` would silently delete formatting the customer sees.
 */
export type ChannelTextFormat = 'markdown' | 'plain'

export const CHANNEL_TEXT_FORMAT: Record<ChannelType, ChannelTextFormat> = {
  web_widget: 'markdown',
  web_demo: 'markdown',
  instagram: 'plain',
  facebook: 'plain',
  whatsapp: 'plain',
  telegram: 'plain',
  tiktok: 'plain',
}

const MARKDOWN_CHANNEL_TYPES: ReadonlySet<string> = new Set(
  Object.entries(CHANNEL_TEXT_FORMAT)
    .filter(([, format]) => format === 'markdown')
    .map(([channelType]) => channelType),
)

/**
 * Accepts a plain `string` by design: several call sites hold the channel type
 * untyped (`ConversationContext.channelType`) or optional (the test-chat
 * route). An unrecognised value answers `false` — "do not treat as Markdown"
 * is the failure direction that cannot corrupt text.
 */
export function isMarkdownChannel(channelType: string): boolean {
  return MARKDOWN_CHANNEL_TYPES.has(channelType)
}
